<script setup>
import { computed } from 'vue';
import InformationIcon from './InformationIcon';
import { useConfiguratorApi } from '../composables/configurator';
import { useMainStore } from '../store/index.js';

const { totalPrice, buyBackValue } = useConfiguratorApi();
const store = useMainStore();

const props = defineProps({
  selectorId: {
    type: String,
    required: true,
  },
  placement: {
    type: String,
    default: 'top',
  },
});

// Sum of prices directly related to the actual truck, selections and the base cost
const productPrice = computed(() => {
  return totalPrice.value.priceBreakdown.basePrice +
    totalPrice.value.priceBreakdown.selectedProductOptionsPrice;
});

// Sum of prices for all services, including repair and maintenance selections and base cost
const servicePrice = computed(() => {
  return totalPrice.value.priceBreakdown.selectedServiceOptionsPrice +
    totalPrice.value.priceBreakdown.repairMaintenancePrice +
    totalPrice.value.priceBreakdown.selectedOptionsRepairMaintenancePrice;
});

const serviceSavings = computed(() => {
  return totalPrice.value.serviceSavings;
});

const showBuyBackValue = computed(() => {
  return store.market === 'pl' && buyBackValue.value;
});

const checkIfBev = computed(() => {
  return store.misc?.template?.trim()?.toUpperCase() === '_BEV';
});

const showDownPaymentTitle = computed(() => {
  return store.options.leasing.find(o => o.id === 'downPayment')?.title || '';
});

const showDownPaymentValue = computed(() => {
  return store.selected.leasing.downPayment.value;
});

// Need to hide buyBack value and operational-leasing-text temporarily for time being
// because Germany was upset with the wrong information being shown in the tooltip and operational-leasing-text.
const showBuyBackValueTemporarily = false;

</script>

<template>
  <sdds-tooltip
    :placement="props.placement"
    :selector="`#${selectorId}`"
    mouse-over-tooltip="true"
    translate="no"
  >
    <ul>
      <li><strong>{{ $i18n('totalPrice') }}:</strong> {{ totalPrice.price }}&nbsp;<span class="sdds-detail-05">{{ $i18n(totalPrice.priceSuffix) }}</span></li>
      <li>- {{ $i18n('truck') }}: {{ productPrice }}&nbsp;<span class="sdds-detail-05">{{ $i18n(totalPrice.priceSuffix) }}</span></li>
      <li>- {{ $i18n('services') }}: {{ servicePrice }}&nbsp;<span class="sdds-detail-05">{{ $i18n(totalPrice.priceSuffix) }}</span></li>
      <template v-if="store.showYearEndOfferBanner">
        <li><span style="font-size: x-small;">{{ $i18n('yearEndOfferPriceHelperText2') }}</span></li>
      </template>
    </ul>
    <template v-if="store.serviceSavings.length">
      <ul>
        <li><strong>{{ $i18n('serviceSavings') }}:</strong></li>
        <template v-if="store.selectedPaymentType === 'leasing'">
          <li>- {{ $i18n('serviceSavingsMonthly') }}: -{{ serviceSavings.serviceSavingsMonthly }}&nbsp;<span class="sdds-detail-05">{{ $i18n(totalPrice.priceSuffix) }}</span></li>
        </template>
        <li>- {{ $i18n('serviceSavingsTotal') }}: -{{ serviceSavings.serviceSavingsTotal }}&nbsp;<span class="sdds-detail-05">{{ $i18n(totalPrice.priceSuffixOneTime) }}</span></li>
      </ul>
    </template>
    <br>
    <ul v-if="showBuyBackValueTemporarily && showBuyBackValue">
      <li>{{ $i18n('priceInfotipNetValue') }}</li>
      <template v-if="checkIfBev">
        <li>{{ $i18n(showDownPaymentTitle) }} {{ showDownPaymentValue }}</li>
      </template>
      <template v-else>
        <li>{{ $i18n('priceInfotipBuyBackValue') }} {{ buyBackValue }}</li>
      </template>
    </ul>
  </sdds-tooltip>
  <InformationIcon :id="selectorId" />
</template>

<style scoped>
  sdds-tooltip > ul {
    padding-inline-start: 0;
    list-style: none;
    margin: 0;
    white-space: pre-wrap;
  }
</style>
